import * as React from "react";
import { useTranslation } from "react-i18next";
import { I18N_NS } from "Config/i18n";
import { MetadataEditInput } from "Components/FileDetails/Components/MetadataEditInput";
import { EditableColumnProps, EditableTable } from "Components/General/EditableTable";
import { MetadataWithValues } from "Components/FileDetails/Components/ChangeMetadata";
import { API } from "General/API";
import { API_URL } from "General/APIEndpoints";
import { projectsDataStore } from "Stores";
import { Button } from "antd";
import { notificationSuccess } from "utils/libHelpers/AntdNotification";
import { ModalComponentProps } from "Components/General/Modal/ModalContext";

interface AddFolderMetadataProps extends ModalComponentProps {
  folderId: number;
}

export const AddFolderMetadata = ({ folderId, setConfirmOnCancel }: AddFolderMetadataProps) => {
  const { t } = useTranslation(I18N_NS.PROJECT);
  const [allMetadata, setAllMetadata] = React.useState<API_Metadata[]>([]);
  const [assignedMetadata, setAssignedMetadata] = React.useState<MetadataWithValues[]>([]);
  const [metadataToSubmit, setMetadataToSubmit] = React.useState<API_FileMetadata[]>([]);
  const [loading, setLoading] = React.useState(false);

  const fetchAvailableMetadata = async () => {
    try {
      setLoading(true);
      const response = await API.getData(API_URL.METADATA.getDefinitionsFull(projectsDataStore.projectId));
      setAllMetadata(response);
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const fetchAssignedMetadata = React.useCallback(async () => {
    try {
      setLoading(true);
      const response = await API.getData<API_FileMetadata[]>(
        API_URL.FOLDERS.getFolderMetadata(projectsDataStore.projectId, folderId)
      );
      // set already assigned value to metadata records
      const metadataForEdit: MetadataWithValues[] = allMetadata.map((data) => {
        const metadataDefinition = response.find((item) => item.definitionId === data.definitionId);
        if (metadataDefinition) {
          return {
            ...data,
            _currentValue: metadataDefinition.value,
          };
        } else {
          return data;
        }
      });
      // because deleting of assigned metadata is made by not-sending it back to server - we set it initially
      setMetadataToSubmit(
        response.map((item) => ({
          definitionId: item.definitionId,
          value: item.value,
          key: item.key,
        }))
      );
      setAssignedMetadata(metadataForEdit);
      // notificationSuccess(t('Success'), t('Metadata updated'));
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  }, [allMetadata, folderId]);

  const editMetadata = async (value) => {
    const metadataToSubmitClone = metadataToSubmit.slice();
    const assignedMetadataClone = assignedMetadata.slice();
    // TODO: check
    const recordIdx = assignedMetadataClone.findIndex((item) => item.definitionId === value.definitionId);
    if (recordIdx === -1) {
      console.warn("Record not found. value = ", JSON.stringify(value));
      return;
    }
    const record = assignedMetadata[recordIdx];
    const newMetadataToSubmit: API_FileMetadata = {
      definitionId: record.definitionId,
      value: value._currentValue,
      key: record.key,
    };
    const existingRecordIdx = metadataToSubmitClone.findIndex(
      (item) => item.definitionId === newMetadataToSubmit.definitionId
    );
    if (existingRecordIdx === -1) {
      metadataToSubmitClone.push(newMetadataToSubmit);
    } else {
      metadataToSubmitClone[existingRecordIdx] = newMetadataToSubmit;
    }
    assignedMetadataClone[recordIdx]._currentValue = value._currentValue;
    setMetadataToSubmit(metadataToSubmitClone);
    setAssignedMetadata(assignedMetadataClone);
    setConfirmOnCancel(true);
  };

  const deleteMetadata = (definitionId: number) => {
    // filter out value in submit object
    const newMetadataToSubmit = metadataToSubmit.filter((item) => item.definitionId !== definitionId);
    // extract metadata value from list of metadata
    const newAssignedMetadata = assignedMetadata.map((item) => {
      if (item.definitionId === definitionId) {
        const { _currentValue, ...withoutValue } = item;
        return withoutValue;
      } else {
        return item;
      }
    });
    setMetadataToSubmit(newMetadataToSubmit);
    setAssignedMetadata(newAssignedMetadata);
  };

  const saveMetadata = async () => {
    try {
      setLoading(true);
      const formBody: API_FileMetadata[] = metadataToSubmit.slice();
      await API.editData(API_URL.FOLDERS.updateFolderMetadata(projectsDataStore.projectId, folderId), formBody);
      notificationSuccess(t("Success"), t("Metadata updated"));
      fetchAssignedMetadata();
      setConfirmOnCancel(false);
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const columns: EditableColumnProps<MetadataWithValues>[] = [
    {
      title: t("Name"),
      dataIndex: "key",
      editable: false,
    },
    {
      title: t("Description"),
      dataIndex: "description",
      editable: false,
    },
    {
      title: t("Value"),
      dataIndex: "_currentValue",
      editable: true,
      customInput: MetadataEditInput,
      inputType: "custom",
      render: (v) => v,
    },
  ];

  React.useEffect(() => {
    if (folderId) {
      fetchAvailableMetadata();
    }
  }, [folderId]);

  React.useEffect(() => {
    if (allMetadata.length) {
      fetchAssignedMetadata();
    }
  }, [allMetadata.length, fetchAssignedMetadata]);

  return (
    <div>
      <EditableTable<MetadataWithValues>
        name="addFolderMetadata"
        rowIdKey="definitionId"
        key="definitionId"
        columns={columns}
        dataSource={assignedMetadata}
        onEdit={editMetadata}
        onDelete={deleteMetadata}
        rowDeletable={(rec) => !!rec._currentValue}
        showAddRow={false}
        loading={loading}
      />
      <div className="text-right mt-1">
        <Button type="primary" loading={loading} onClick={saveMetadata}>
          {t("Save")}
        </Button>
      </div>
    </div>
  );
};
